/**
 * Alerts Panel Component
 * Shows critical alerts and notifications
 */
import { AlertTriangle, AlertCircle, Info, XCircle, Bell, Loader } from 'lucide-react';

const AlertsPanel = ({ data, isFullScreen }) => {
  if (!data) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <Bell className="w-6 h-6 mr-2 text-blue-600" />
          Active Alerts
        </h2>
        <div className="text-center py-8 text-gray-500">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-2" />
          <p>Loading alerts...</p>
        </div>
      </div>
    );
  }

  const alerts = Array.isArray(data) ? data : data.alerts || [];
  const criticalCount = alerts.filter(a => a.severity === 'CRITICAL').length;
  const warningCount = alerts.filter(a => a.severity === 'WARNING').length;

  const getSeverityConfig = (severity) => {
    const configs = {
      CRITICAL: {
        bg: 'bg-red-50',
        border: 'border-red-500',
        text: 'text-red-800',
        icon: XCircle,
        iconColor: 'text-red-600',
        badge: 'bg-red-500 text-white',
      },
      WARNING: {
        bg: 'bg-yellow-50',
        border: 'border-yellow-500',
        text: 'text-yellow-800',
        icon: AlertTriangle,
        iconColor: 'text-yellow-600',
        badge: 'bg-yellow-500 text-white',
      },
      INFO: {
        bg: 'bg-blue-50',
        border: 'border-blue-500',
        text: 'text-blue-800',
        icon: Info,
        iconColor: 'text-blue-600',
        badge: 'bg-blue-500 text-white',
      },
    };
    return configs[severity] || configs.INFO;
  };

  const getAlertTypeLabel = (type) => {
    const labels = {
      SLA_BREACH: 'SLA Breach',
      SLA_AT_RISK: 'SLA At Risk',
      QC_FAILED: 'QC Failed',
      SAFETY_VIOLATION: 'Safety Violation',
      DOCK_BLOCKED: 'Dock Blocked',
      SYSTEM_ERROR: 'System Error',
      WORKFLOW_EXCEPTION: 'Workflow Exception',
    };
    return labels[type] || type;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Just now';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className={`font-bold text-gray-800 flex items-center ${isFullScreen ? 'text-lg' : 'text-xl'}`}>
          <Bell className="w-6 h-6 mr-2 text-blue-600" />
          Active Alerts
        </h2>
        <div className="flex items-center space-x-2">
          {criticalCount > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              {criticalCount} Critical
            </span>
          )}
          {warningCount > 0 && (
            <span className="bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              {warningCount} Warning
            </span>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      {alerts.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-red-50 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-red-600">{criticalCount}</div>
            <div className="text-xs text-gray-600">Critical</div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-yellow-600">{warningCount}</div>
            <div className="text-xs text-gray-600">Warning</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-blue-600">
              {alerts.length - criticalCount - warningCount}
            </div>
            <div className="text-xs text-gray-600">Info</div>
          </div>
        </div>
      )}

      {/* Alerts List */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {alerts.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <AlertCircle className="w-12 h-12 mx-auto mb-2 text-gray-400" />
            <p className="text-sm">No active alerts</p>
            <p className="text-xs text-gray-400">All systems operational</p>
          </div>
        ) : (
          alerts.map((alert, index) => {
            const config = getSeverityConfig(alert.severity);
            const Icon = config.icon;

            return (
              <div
                key={alert.id || index}
                className={`${config.bg} border-l-4 ${config.border} rounded-lg p-4 hover:shadow-md transition-shadow`}
              >
                <div className="flex items-start space-x-3">
                  <Icon className={`w-5 h-5 ${config.iconColor} flex-shrink-0 mt-0.5`} />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-1">
                      <span className={`font-semibold text-sm ${config.text}`}>
                        {getAlertTypeLabel(alert.type)}
                      </span>
                      <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                        {formatTime(alert.created_at || alert.timestamp)}
                      </span>
                    </div>

                    <p className="text-sm text-gray-700 mb-2">
                      {alert.message || 'No details available'}
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="text-xs text-gray-600 space-y-0.5">
                        {alert.truck_number && (
                          <div>Truck: <span className="font-semibold">{alert.truck_number}</span></div>
                        )}
                        {alert.workflow_id && (
                          <div>Workflow: <span className="font-mono text-xs">{alert.workflow_id}</span></div>
                        )}
                        {alert.location && (
                          <div>Location: {alert.location}</div>
                        )}
                      </div>

                      {alert.action_required && (
                        <button className="text-xs bg-white border border-gray-300 hover:bg-gray-50 px-3 py-1 rounded-md font-medium transition-colors">
                          Take Action
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* View All Link */}
      {alerts.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200 text-center">
          <button className="text-sm text-blue-600 hover:text-blue-800 font-medium">
            View All Alerts ({alerts.length})
          </button>
        </div>
      )}
    </div>
  );
};

export default AlertsPanel;
